/**
 * Module Vuex pour les statistiques
 * Gère l'état des statistiques de réservations, offres et bagages
 */
import { statisticsService } from '@/services';

const state = {
  // Statistiques de réservations
  reservationStats: null,
  todayStats: null,
  weekStats: null,
  monthStats: null,
  yearStats: null,
  
  // Statistiques d'offres
  offerStats: null,
  
  // Statistiques de bagages
  luggageStats: null,
  
  // État de chargement
  loading: false,
  error: null,
  
  // Cache
  lastFetch: null,
  cacheDuration: 5 * 60 * 1000, // 5 minutes
};

const getters = {
  reservationStats: (state) => state.reservationStats,
  todayStats: (state) => state.todayStats,
  weekStats: (state) => state.weekStats,
  monthStats: (state) => state.monthStats,
  yearStats: (state) => state.yearStats,
  offerStats: (state) => state.offerStats,
  luggageStats: (state) => state.luggageStats,
  isLoading: (state) => state.loading,
  hasError: (state) => state.error !== null,
  
  // Getters calculés
  totalRevenue: (state) => {
    if (!state.reservationStats || !state.reservationStats.items) return 0;
    return state.reservationStats.items.reduce((sum, item) => {
      return sum + (item.montantTotal || 0);
    }, 0);
  },
  
  totalReservations: (state) => {
    if (!state.reservationStats || !state.reservationStats.items) return 0;
    return state.reservationStats.items.length;
  },
};

const mutations = {
  SET_RESERVATION_STATS(state, stats) {
    state.reservationStats = stats;
  },
  SET_TODAY_STATS(state, stats) {
    state.todayStats = stats;
  },
  SET_WEEK_STATS(state, stats) {
    state.weekStats = stats;
  },
  SET_MONTH_STATS(state, stats) {
    state.monthStats = stats;
  },
  SET_YEAR_STATS(state, stats) {
    state.yearStats = stats;
  },
  SET_OFFER_STATS(state, stats) {
    state.offerStats = stats;
  },
  SET_LUGGAGE_STATS(state, stats) {
    state.luggageStats = stats;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_LAST_FETCH(state, timestamp) {
    state.lastFetch = timestamp;
  },
  CLEAR_STATS(state) {
    state.reservationStats = null;
    state.todayStats = null;
    state.weekStats = null;
    state.monthStats = null;
    state.yearStats = null;
    state.offerStats = null;
    state.luggageStats = null;
    state.lastFetch = null;
  },
};

const actions = {
  /**
   * Récupérer les statistiques de réservations
   */
  async fetchReservationStatistics({ commit }, { period, section, data = {} }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const stats = await statisticsService.getReservationStatistics(period, section, data);
      commit('SET_RESERVATION_STATS', stats);
      commit('SET_LAST_FETCH', Date.now());
      return stats;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Récupérer les statistiques du jour
   */
  async fetchTodayStatistics({ commit }, data = {}) {
    commit('SET_LOADING', true);
    try {
      const stats = await statisticsService.getTodayStatistics(data);
      commit('SET_TODAY_STATS', stats);
      return stats;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Récupérer les statistiques de la semaine
   */
  async fetchWeekStatistics({ commit }, data = {}) {
    commit('SET_LOADING', true);
    try {
      const stats = await statisticsService.getWeekStatistics(data);
      commit('SET_WEEK_STATS', stats);
      return stats;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Récupérer les statistiques du mois
   */
  async fetchMonthStatistics({ commit }, data = {}) {
    commit('SET_LOADING', true);
    try {
      const stats = await statisticsService.getMonthStatistics(data);
      commit('SET_MONTH_STATS', stats);
      return stats;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Récupérer les statistiques de l'année
   */
  async fetchYearStatistics({ commit }, data = {}) {
    commit('SET_LOADING', true);
    try {
      const stats = await statisticsService.getYearStatistics(data);
      commit('SET_YEAR_STATS', stats);
      return stats;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Récupérer les statistiques d'une offre
   */
  async fetchOfferStatistics({ commit }, { period, data = {} }) {
    commit('SET_LOADING', true);
    try {
      const stats = await statisticsService.getOfferStatistics(period, data);
      commit('SET_OFFER_STATS', stats);
      return stats;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Récupérer les statistiques des bagages
   */
  async fetchLuggageStatistics({ commit }, { period, data = {} }) {
    commit('SET_LOADING', true);
    try {
      const stats = await statisticsService.getLuggageStatistics(period, data);
      commit('SET_LUGGAGE_STATS', stats);
      return stats;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Récupérer les statistiques pour une période personnalisée
   */
  async fetchCustomPeriodStatistics({ commit }, data = {}) {
    commit('SET_LOADING', true);
    try {
      const stats = await statisticsService.getCustomPeriodStatistics(data);
      commit('SET_RESERVATION_STATS', stats);
      return stats;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Effacer toutes les statistiques
   */
  clearStatistics({ commit }) {
    commit('CLEAR_STATS');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
